import React, { useEffect, useState, useCallback, useMemo } from "react";
import Stack from "components/common/Stack";
import IconButton from "components/common/IconButton";
import Box from "components/common/Box";
import Modal from "components/common/Modal";
import TextField from "components/common/TextField";
import JarvisButton from "components/JarvisButton";
import JarvisAutoSearch from "components/JarvisAutoSearch";
import DatePicker from "components/common/DatePicker";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";
import { debounce } from "lodash";

// Import icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";

// For responsive design
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { commonActions } from "stores/common/commonActions";
import Text from "components/common/Text";
import {
  CircularProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Constants
const MIN_STOCKS = 1;
const DEBOUNCE_DELAY = 300;
const GENERATE_STATUS_LINES = [
  "Checking holdings…",
  "Matching stocks in our database…",
  "Running portfolio analysis…",
  "Building your health report…",
];

const money = (value) =>
  formatHelper.currency(value, {
    config: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  });

const formatBuyDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function PreviewPortfolio({ onDataUpdate, onNextStep }) {
  const [previewData, setPreviewData] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    stock: null,
    quantity: "",
    buyPrice: "",
    buyDate: null,
  });
  const [formErrors, setFormErrors] = useState({
    stock: false,
    quantity: false,
    buyPrice: false,
    buyDate: false,
  });
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateStatusIndex, setGenerateStatusIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const idGetUploadedExcel = useSelector(
    (state) => state.PHC.PHCOnboarding.portfolioId,
  );

  const { completedPortfolioId, activePortfolioId, justUploadedPortfolioId } =
    useSelector((state) => state.PHC.PHCDashboard);

  // Calculate total investment whenever previewData changes
  useEffect(() => {
    const total = previewData.reduce((sum, item) => {
      return sum + (item.quantity * item.buyPrice || 0);
    }, 0);
    setTotalInvestment(total);
  }, [previewData]);

  // Memoized data transformation for stock list
  const transformStockList = useCallback((data) => {
    if (!data?.result || !Array.isArray(data.result)) return [];

    return data.result.map((stock) => ({
      label: `${stock.stockName || stock.companyName || stock.name} (${
        stock.symbol || stock.stockCode
      })`,
      value: stock.symbol || stock.stockCode,
      stockName: stock.stockName || stock.companyName || stock.name,
      symbol: stock.symbol || stock.stockCode,
      ...stock,
    }));
  }, []);

  // Memoized fetch data function
  const fetchData = useCallback(async () => {
    const portfolioId =
      activePortfolioId ||
      idGetUploadedExcel ||
      justUploadedPortfolioId ||
      completedPortfolioId;

    if (!portfolioId) {
      return;
    }

    setIsFetching(true);

    try {
      const [portfolioRes, stockRes] = await Promise.allSettled([
        PHCOnboardingApis.getUploadedExcelPreview(portfolioId),
        PHCOnboardingApis.getCompanyListPreview(),
      ]);

      // Handle portfolio data
      if (portfolioRes.status === "fulfilled") {
        const portfolioData = portfolioRes.value?.result || [];

        setPreviewData(portfolioData);

        // Notify parent component if callback provided
        if (onDataUpdate) {
          onDataUpdate(portfolioData);
        }
      } else {
        console.error("Failed to load portfolio:", portfolioRes.reason);
        dispatch(
          commonActions.setToast({
            message: "Failed to load portfolio data",
            type: "error",
          }),
        );
      }

      // Handle stock list data
      if (stockRes.status === "fulfilled") {
        const transformedList = transformStockList(stockRes.value);

        setStockList(transformedList);
      } else {
        console.warn("Failed to load stock list:", stockRes.reason);
      }
    } catch (error) {
      console.error("Unexpected error fetching data:", error);
      dispatch(
        commonActions.setToast({
          message: "An unexpected error occurred",
          type: "error",
        }),
      );
    } finally {
      setIsFetching(false);
    }
  }, [
    idGetUploadedExcel,
    justUploadedPortfolioId,
    dispatch,
    transformStockList,
    onDataUpdate,
    completedPortfolioId,
    activePortfolioId,
  ]);

  // Debounced fetch to prevent rapid calls
  const debouncedFetch = useMemo(
    () => debounce(fetchData, DEBOUNCE_DELAY),
    [fetchData],
  );

  useEffect(() => {
    if (
      idGetUploadedExcel ||
      justUploadedPortfolioId ||
      completedPortfolioId ||
      activePortfolioId
    ) {
      debouncedFetch();
    }

    // Cleanup debounce on unmount
    return () => {
      debouncedFetch.cancel();
    };
  }, [
    idGetUploadedExcel,
    justUploadedPortfolioId,
    debouncedFetch,
    completedPortfolioId,
    activePortfolioId,
  ]);

  useEffect(() => {
    if (!isGenerating) {
      setGenerateStatusIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setGenerateStatusIndex((i) => (i + 1) % GENERATE_STATUS_LINES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [isGenerating]);

  // Handle delete with confirmation
  const handleDelete = useCallback(
    async (rowData) => {
      if (previewData.length <= MIN_STOCKS) {
        dispatch(
          commonActions.setToast({
            message: `Cannot delete. At least ${MIN_STOCKS} stock must remain in the portfolio.`,
            type: "warning",
          }),
        );
        return;
      }

      const itemId = rowData.portfolioDetailsId;
      if (!itemId) {
        dispatch(
          commonActions.setToast({
            message: "Cannot delete: Invalid item ID",
            type: "error",
          }),
        );
        return;
      }

      setDeletingId(itemId);
      setIsDeleting(true);

      try {
        // Optimistic update
        const newData = previewData.filter(
          (item) => item.portfolioDetailsId !== itemId,
        );
        setPreviewData(newData);

        await PHCOnboardingApis.deletePortfolioItem(itemId);

        dispatch(
          commonActions.setToast({
            message: `${rowData.stockName} deleted successfully`,
            type: "success",
          }),
        );
      } catch (error) {
        console.error("Delete failed:", error);

        // Revert optimistic update on error
        fetchData();

        dispatch(
          commonActions.setToast({
            message: error.message || "Failed to delete. Please try again.",
            type: "error",
          }),
        );
      } finally {
        setDeletingId(null);
        setIsDeleting(false);
      }
    },
    [previewData, dispatch, fetchData],
  );

  // Handle edit action
  const handleEdit = useCallback(
    (rowData) => {
      const matchingStock = stockList.find(
        (stock) => stock.symbol === rowData.symbol,
      ) || {
        label: `${rowData.stockName} (${rowData.symbol})`,
        value: rowData.symbol,
        stockName: rowData.stockName,
        symbol: rowData.symbol,
      };

      const buyDateValue = rowData.buyDate
        ? new Date(rowData.buyDate)
        : new Date();

      setFormData({
        stock: matchingStock,
        quantity: rowData.quantity?.toString() || "",
        buyPrice: rowData.buyPrice?.toString() || "",
        buyDate: buyDateValue,
      });

      setFormErrors({
        stock: false,
        quantity: false,
        buyPrice: false,
        buyDate: false,
      });

      setEditingRow(rowData);
      setEditModalOpen(true);
    },
    [stockList],
  );

  // Handle form input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Validate numeric fields
    if ((name === "quantity" || name === "buyPrice") && value !== "") {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue <= 0) {
        setFormErrors((prev) => ({ ...prev, [name]: true }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  }, []);

  const handleStockChange = useCallback((newValue) => {
    setFormData((prev) => ({ ...prev, stock: newValue }));
    setFormErrors((prev) => ({ ...prev, stock: false }));
  }, []);

  const handleDateChange = useCallback(
    (newDate) => {
      if (newDate > new Date()) {
        setFormErrors((prev) => ({ ...prev, buyDate: true }));
        dispatch(
          commonActions.setToast({
            message: "Buy date cannot be in the future",
            type: "warning",
          }),
        );
        return;
      }

      setFormData((prev) => ({ ...prev, buyDate: newDate }));
      setFormErrors((prev) => ({ ...prev, buyDate: false }));
    },
    [dispatch],
  );

  // Form validation
  const validateForm = useCallback(() => {
    const errors = {
      stock: !formData.stock,
      quantity: !formData.quantity || parseInt(formData.quantity) <= 0,
      buyPrice: !formData.buyPrice || parseFloat(formData.buyPrice) <= 0,
      buyDate: !formData.buyDate || formData.buyDate > new Date(),
    };

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  }, [formData]);

  // Save edit - FIXED VERSION
  const handleSaveEdit = useCallback(async () => {
    if (!validateForm()) {
      dispatch(
        commonActions.setToast({
          message: "Please check all fields and try again",
          type: "warning",
        }),
      );
      return;
    }

    setLoading(true);
    try {
      const formatDateForAPI = (date) => {
        return dateTimeHelper.sipDate(new Date(date));
      };

      const payload = {
        portfolioData: [
          {
            portfolioDetailsId: editingRow.portfolioDetailsId,
            portfolioId: editingRow.portfolioId,
            symbol: formData.stock.symbol,
            stockName: formData.stock.stockName,
            quantity: parseInt(formData.quantity, 10),
            buyPrice: parseFloat(formData.buyPrice),
            buyDate: formatDateForAPI(formData.buyDate),
          },
        ],
      };

      await PHCOnboardingApis.uploadedExcelUpdate(payload);

      // Update local state IMMEDIATELY with new values
      setPreviewData((prev) => {
        const updatedData = prev.map((item) =>
          item.portfolioDetailsId === editingRow.portfolioDetailsId
            ? {
                ...item,
                stockName: formData.stock.stockName,
                symbol: formData.stock.symbol,
                quantity: parseInt(formData.quantity, 10),
                buyPrice: parseFloat(formData.buyPrice),
                buyDate: formatDateForAPI(formData.buyDate),
                // Ensure all necessary fields are included
                portfolioDetailsId: item.portfolioDetailsId,
                portfolioId: item.portfolioId,
              }
            : item,
        );
        console.log("Updated preview data:", updatedData);
        return updatedData;
      });

      dispatch(
        commonActions.setToast({
          message: `${formData.stock.stockName} updated successfully`,
          type: "success",
        }),
      );

      handleCloseModal();
    } catch (error) {
      console.error("Update error:", error);
      dispatch(
        commonActions.setToast({
          message:
            error.response?.data?.message || "Update failed. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [validateForm, editingRow, formData, dispatch]);

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingRow(null);
    setFormData({
      stock: null,
      quantity: "",
      buyPrice: "",
      buyDate: null,
    });
    setFormErrors({
      stock: false,
      quantity: false,
      buyPrice: false,
      buyDate: false,
    });
  }, []);

  const supportedStocksCount = useMemo(() => {
    return previewData.filter((s) => !s.errorMessage).length;
  }, [previewData]);

  const holdingsFooter = previewData.length > 0 ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: 1.5,
        borderTop: "1px solid #E5E7EB",
        bgcolor: "#F8FAFC",
      }}
    >
      <Text sx={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>
        {previewData.length} stock{previewData.length !== 1 ? "s" : ""}
      </Text>
      <Stack alignItems="flex-end" spacing={0}>
        <Text sx={{ fontSize: 11, color: "#6B7280" }}>Total investment</Text>
        <Text sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
          {money(totalInvestment)}
        </Text>
      </Stack>
    </Box>
  ) : null;

  // Mobile card view
  const MobileCardView = useMemo(() => {
    if (isFetching) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Text variant="body2" color="text.secondary" mt={2}>
            Loading portfolio...
          </Text>
        </Box>
      );
    }

    if (!previewData || previewData.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <InfoIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <Text variant="body1" color="text.secondary">
            No portfolio items found
          </Text>
          <Text variant="body2" color="text.secondary" mt={1}>
            Add stocks to get started
          </Text>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        {previewData.map((item) => {
          const itemId = item.portfolioDetailsId;
          const isThisItemDeleting = deletingId === itemId;
          const totalValue = (item.quantity || 0) * (item.buyPrice || 0);
          const hasError = Boolean(item.errorMessage);

          return (
            <Box
              key={itemId}
              sx={{
                border: "1px solid #E5E7EB",
                borderLeft: hasError ? "3px solid #E11D48" : "1px solid #E5E7EB",
                borderRadius: "16px",
                padding: "16px",
                bgcolor: hasError
                  ? "rgba(225,29,72,0.06)"
                  : isThisItemDeleting
                    ? "#F9FAFB"
                    : "#FFFFFF",
                boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
                opacity: isThisItemDeleting ? 0.7 : 1,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Stack>
                    <Text
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#111827",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {item.stockName}
                    </Text>
                    <Text
                      sx={{
                        fontSize: 12,
                        color: "#6B7280",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {item.symbol}
                    </Text>
                  </Stack>
                  <Stack alignItems="flex-end">
                    <Text
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#111827",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {money(totalValue)}
                    </Text>
                    <Text sx={{ fontSize: 11, color: "#6B7280" }}>
                      Total value
                    </Text>
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Text sx={{ fontSize: 12, color: "#6B7280" }}>Quantity</Text>
                    <Text
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111827",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {item.quantity?.toLocaleString()}
                    </Text>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Text sx={{ fontSize: 12, color: "#6B7280" }}>Buy price</Text>
                    <Text
                      sx={{
                        fontSize: 13,
                        color: "#111827",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {money(item.buyPrice)}
                    </Text>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Text sx={{ fontSize: 12, color: "#6B7280" }}>Buy date</Text>
                    <Text
                      sx={{
                        fontSize: 13,
                        color: "#111827",
                        opacity: isThisItemDeleting ? 0.5 : 1,
                      }}
                    >
                      {formatBuyDate(item.buyDate)}
                    </Text>
                  </Stack>
                </Stack>

                {/* <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(item)}
                    aria-label={`Edit ${item.stockName}`}
                    title="Edit"
                    disabled={isDeleting || isThisItemDeleting}
                    sx={{
                      backgroundColor: "primary.light",
                      "&:hover": { backgroundColor: "primary.main" },
                      padding: "6px",
                    }}
                  >
                    <EditIcon fontSize="small" sx={{ color: "white" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item)}
                    aria-label={`Delete ${item.stockName}`}
                    title="Delete"
                    disabled={
                      isThisItemDeleting || previewData.length <= MIN_STOCKS
                    }
                    sx={{
                      backgroundColor: isThisItemDeleting
                        ? "grey.400"
                        : "error.light",
                      "&:hover": {
                        backgroundColor: isThisItemDeleting
                          ? "grey.400"
                          : "error.main",
                      },
                      padding: "6px",
                    }}
                  >
                    {isThisItemDeleting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DeleteIcon fontSize="small" sx={{ color: "white" }} />
                    )}
                  </IconButton>
                </Stack> */}
              </Stack>
            </Box>
          );
        })}
        {holdingsFooter}
      </Stack>
    );
  }, [
    previewData,
    deletingId,
    isDeleting,
    isFetching,
    handleEdit,
    handleDelete,
    holdingsFooter,
  ]);

  const handleNextStep = useCallback(async () => {
    if (previewData.length === 0) {
      dispatch(
        commonActions.setToast({
          message: "Please add at least one stock to continue",
          type: "warning",
        }),
      );
      return;
    }

    try {
      setIsGenerating(true);
      await PHCOnboardingApis.phcOnboardPreview();

      if (completedPortfolioId || activePortfolioId) {
        const payload = {
          portfolioId: completedPortfolioId
            ? completedPortfolioId
            : activePortfolioId,
        };
        await PHCOnboardingApis.phcHealthGenerate(payload);
      }

      dispatch(PHCOnboardingActions.getOnboardingData());

      if (onNextStep) {
        onNextStep();
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      dispatch(
        commonActions.setToast({
          message:
            error.response?.data?.message || "Failed to continue onboarding",
          type: "error",
        }),
      );
    } finally {
      setIsGenerating(false);
    }
  }, [
    previewData,
    dispatch,
    onNextStep,
    completedPortfolioId,
    activePortfolioId,
  ]);

  const handleConfirmContinue = async () => {
    setConfirmModalOpen(false);
    await handleNextStep();
  };


  const headerCellSx = {
    bgcolor: "#F8FAFC",
    color: "#6B7280",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: "1px solid #E5E7EB",
    py: 1.5,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const bodyCellSx = {
    borderBottom: "1px solid #F3F4F6",
    py: 1.75,
    fontSize: 13,
    color: "#111827",
  };

  return (
    <>
      <Stack mt={3} spacing={3}>
        {supportedStocksCount < previewData.length && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              backgroundColor: "#FFD0D0",
              borderRadius: "8px",
              padding: "16px",
            }}
            mb={2}
          >
            <WarningAmberIcon color="warning" />
            <Text
              variant="body2"
              fontWeight="medium"
              color="#AE0707;"
            >
              Highlighted stocks will be excluded from the analysis as the
              required data is currently inaccessible.{" "}
            </Text>
          </Stack>
        )}

        {isFetching && !isMobile && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Text variant="body2" color="text.secondary" mt={2}>
              Loading portfolio data...
            </Text>
          </Box>
        )}

        {isMobile ? (
          MobileCardView
        ) : previewData.length > 0 ? (
          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "16px",
              overflow: "hidden",
              bgcolor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
            }}
          >
            <TableContainer sx={{ overflowX: "auto", maxHeight: 560 }}>
              <MuiTable sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Stock</TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      Qty
                    </TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      Buy price
                    </TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      Buy date
                    </TableCell>
                    <TableCell align="right" sx={headerCellSx}>
                      Total value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((item) => {
                    const hasError = Boolean(item.errorMessage);
                    const isThisItemDeleting =
                      deletingId === item.portfolioDetailsId;
                    const total = (item.quantity || 0) * (item.buyPrice || 0);
                    return (
                      <TableRow
                        key={item.portfolioDetailsId}
                        sx={{
                          bgcolor: hasError
                            ? "rgba(225,29,72,0.06)"
                            : "transparent",
                          "&:hover": {
                            bgcolor: hasError
                              ? "rgba(225,29,72,0.09)"
                              : "#F8FAFC",
                          },
                          opacity: isThisItemDeleting ? 0.55 : 1,
                          "& td:first-of-type": {
                            borderLeft: hasError
                              ? "3px solid #E11D48"
                              : "3px solid transparent",
                          },
                        }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Stack spacing={0.25}>
                            <Text
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#111827",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.stockName}
                            </Text>
                            <Text sx={{ fontSize: 12, color: "#6B7280" }}>
                              {item.symbol}
                            </Text>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          {item.quantity?.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          {money(item.buyPrice)}
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          {formatBuyDate(item.buyDate)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...bodyCellSx, fontWeight: 700 }}
                        >
                          {money(total)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MuiTable>
            </TableContainer>
            {holdingsFooter}
          </Box>
        ) : (
          !isFetching && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: "12px",
                backgroundColor: "grey.50",
              }}
            >
              <InfoIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Text variant="h6" color="text.secondary" gutterBottom>
                No Stocks Added
              </Text>
            </Box>
          )
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <JarvisButton
            variant="contained"
            onClick={() => setConfirmModalOpen(true)}
            sx={{ minWidth: "140px" }}
            disabled={isDeleting || previewData.length === 0 || isGenerating}
            loading={isGenerating}
          >
            Continue
          </JarvisButton>
        </Stack>
      </Stack>

      {isGenerating ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(15,23,42,0.45)",
            px: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              bgcolor: "#FFFFFF",
              borderRadius: "16px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 20px 50px rgba(15,23,42,0.22)",
              p: { xs: 3, md: 4 },
              textAlign: "center",
            }}
          >
            <CircularProgress size={36} sx={{ color: "#2977EC", mb: 2 }} />
            <Text
              sx={{
                display: "block",
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
                mb: 1,
              }}
            >
              Generating your health report
            </Text>
            <Text
              sx={{
                display: "block",
                fontSize: 14,
                color: "#6B7280",
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              This can take a minute. Please don’t close this page.
            </Text>
            <Text sx={{ fontSize: 13, fontWeight: 500, color: "#2977EC" }}>
              {GENERATE_STATUS_LINES[generateStatusIndex]}
            </Text>
          </Box>
        </Box>
      ) : null}

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        size="sm"
        disableClose={true}
        disableBackdropClick={loading}
      >
        <Stack spacing={3} p={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="h6" fontWeight="bold" color="primary.main">
              Edit Stock
            </Text>
            <IconButton
              onClick={handleCloseModal}
              size="small"
              disabled={loading}
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack spacing={2}>
            {/* <Box>
              <JarvisAutoSearch
                label="Stock"
                options={stockList}
                value={formData.stock}
                onChange={handleStockChange}
                placeholder="Search and select stock"
                error={formErrors.stock}
                helperText={formErrors.stock ? "Please select a stock" : ""}
                disabled={loading}
                required
              />
            </Box> */}

            <Box>
              <TextField
                label="Quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                error={formErrors.quantity}
                helperText={formErrors.quantity ? "Enter valid quantity" : ""}
                type="number"
                min="1"
                fullWidth
                disabled={loading}
                required
              />
            </Box>

            <Box>
              <TextField
                label="Buy Price"
                name="buyPrice"
                value={formData.buyPrice}
                onChange={handleInputChange}
                error={formErrors.buyPrice}
                helperText={formErrors.buyPrice ? "Enter valid price" : ""}
                type="number"
                min="0.01"
                step="0.01"
                fullWidth
                disabled={loading}
                required
              />
            </Box>

            <Box>
              <DatePicker
                label="Buy Date"
                value={formData.buyDate}
                onChange={handleDateChange}
                error={formErrors.buyDate}
                helperText={formErrors.buyDate ? "Enter valid date" : ""}
                maxDate={new Date()}
                disabled={loading}
                required
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
            <JarvisButton
              variant="outlined"
              onClick={handleCloseModal}
              disabled={loading}
              disableEndIcon={true}
            >
              Cancel
            </JarvisButton>
            <JarvisButton
              variant="contained"
              onClick={handleSaveEdit}
              disabled={loading}
              loading={loading}
              disableEndIcon={true}
            >
              Save Changes
            </JarvisButton>
          </Stack>
        </Stack>
      </Modal>

      <Modal open={confirmModalOpen} size="sm" disableClose>
        <Stack spacing={3} p={4} alignItems="center">
          <Text variant="body1" align="center">
            Your Portfolio Health Check report will only consider the{" "}
            <b>{supportedStocksCount}</b> stocks available in our database for
            analysis.
          </Text>

          <Stack direction="row" spacing={2} mt={2}>
            <JarvisButton
              variant="outlined"
              onClick={() => setConfirmModalOpen(false)}
              sx={{ minWidth: "120px" }}
              disableEndIcon={true}
            >
              Cancel
            </JarvisButton>

            <JarvisButton
              variant="contained"
              onClick={handleConfirmContinue}
              loading={isGenerating}
              disabled={isGenerating}
              sx={{ minWidth: "120px" }}
              disableEndIcon={true}
            >
              Continue
            </JarvisButton>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
}

export default React.memo(PreviewPortfolio);
